// 모바일 메신저(MM) 팝업 — 열리면 최상위 history 에 한 칸 쌓고, 뒤로가기(popstate)로 «맨 위 팝업만» 닫는다.
// 설계: aigw-designs/patch_mm_mobile_menu_back_close.md
//
// 왜: MM 의 팝업(모바일 메뉴 바텀시트·모달·팝오버·드롭다운)은 어느 것도 브라우저 history 에 칸을
// 쌓지 않는다 → 팝업이 열린 채 뒤로가기를 하면 팝업이 아니라 «메신저 자체»를 빠져나가 직전 메뉴
// (예: 드라이브)로 간다. MM 은 외부 앱이라 마크업을 못 고치므로, 같은 오리진 iframe 문서를
// 관찰해 «열림»을 감지하고 우리가 칸을 쌓는다(EmbedPage 의 기존 DOM 보정 선례와 같은 방식).
//
// 🔴 전부 try/catch — 셀렉터가 어긋나거나 MM 이 바뀌면 조용히 무동작 = 지금과 같은 동작으로 돌아간다.
// 🔴 합성 이벤트는 전부 bubbles:true 다 — 폴백 넷이 모두 버블에 기댄다(K3 RootClose·K4 useDismiss 는
//    document 리스너, K1 `.modal` onClick·K2 Backdrop onClick 은 React 위임 리스너).
// 🔴 이벤트는 doc.defaultView(iframe 쪽) 생성자로 만든다 — 최상위 창 생성자로 만들면 iframe 안
//    instanceof 검사·React 합성이벤트 경로에서 어긋날 수 있다.
export const STATE_KEY = 'gwMmMenuBack';
export const THROTTLE_MS = 100;      // 옵저버 콜백 쓰로틀(진행 중 타이머가 있으면 새로 걸지 않는다)
export const CLOSE_WAIT_MS = 150;    // 1차 → 폴백 → 포기 사이 간격
export const RESYNC_MS = 350;        // 닫힘 애니메이션 뒤 재점검(겹친 팝업이 남았으면 다시 한 칸)
export const IGNORE_POP_MS = 1000;   // 우리가 부른 back() 의 메아리 popstate 를 무시하는 시한

function hasClass(el, name) {
  try { return !!(el && el.classList && el.classList.contains(name)); } catch (e) { return false; }
}

// 닫혀도 DOM 에 남는 계열(K3)만 실제로 보이는지까지 본다(jsdom 은 레이아웃이 없어 시험이 스텁한다).
function isVisible(el) {
  try { return typeof el.getClientRects === 'function' ? el.getClientRects().length > 0 : true; } catch (e) { return false; }
}

// Escape 를 받을 «루트 안 첫 요소». 없으면 루트 자신(어차피 버블로 같은 리스너에 닿는다).
function innerTarget(root) {
  try { return root.firstElementChild || root; } catch (e) { return root; }
}

// MM 11 이 팝업을 그리는 네 방식. 각 계열의 «열림 판정(원판정)»과 닫는 법(1차·폴백)을 한곳에 둔다.
// 🔴 `.plugin-link-tooltip-floating-overlay` 는 넣지 않는다 — pointer-events:none 링크 툴팁이라 팝업이 아니다.
export const POPUP_KINDS = [
  {
    key: 'K1',                                   // react-bootstrap 모달(모바일 메뉴 .menuModal·GenericModal·확인창)
    sel: '.modal',
    open: (el) => hasClass(el, 'in') || hasClass(el, 'show'),   // Fade 는 닫힐 때 'in' 을 바로 뗀다
    needVisible: false,
    fallback: (doc, root) => ({ target: root, types: ['mousedown', 'mouseup', 'click'] }),
  },
  {
    key: 'K2',                                   // MUI Modal·Popover·Menu
    sel: '.MuiModal-root,.MuiPopover-root',
    open: (el) => !hasClass(el, 'MuiModal-hidden') && el.getAttribute('aria-hidden') !== 'true',
    needVisible: false,
    // backdrop 이 없으면 폴백이 없다 → 그대로 포기(stubborn)해 사용자를 뒤로가기에 가두지 않는다.
    fallback: (doc, root) => {
      const backdrop = root.querySelector('.MuiBackdrop-root');
      return backdrop ? { target: backdrop, types: ['mousedown', 'mouseup', 'click'] } : null;
    },
  },
  {
    key: 'K3',                                   // RootClose 오버레이(부트스트랩 팝오버·드롭다운·옛 MenuWrapper 메뉴)
    sel: '.popover,.open > .dropdown-menu,.dropdown-menu.show,.MenuWrapper--open .dropdown-menu',
    open: () => true,                            // 선택자 자체가 열림 판정이다
    needVisible: true,                           // 닫혀도 DOM 에 남는 계열
    keyup: true,                                 // RootClose 는 keyup 으로 Escape 를 받는다
    fallback: (doc) => ({ target: doc.documentElement, types: ['mousedown', 'click'] }),
  },
  {
    key: 'K4',                                   // Floating UI 팝오버(프로필·그룹멘션·채널헤더 설명·이모지 선택기)
    sel: '.user-profile-popover-floating-overlay,.user-group-popover-floating-overlay,'
      + '.channel-header-text-popover-floating-overlay,.emoji-picker-overlay',
    open: () => true,                            // 이 오버레이는 열릴 때만 붙는다
    needVisible: false,
    topFirst: true,                              // 🔴 맨 위 선택에서 우선 — 모달 «안»에서 열리는데 DOM 순서는 모달보다 앞이다
    // useDismiss 기본 outsidePressEvent = 'pointerdown'. PointerEvent 가 없으면(jsdom) MouseEvent 로 만든다.
    fallback: (doc, root) => ({ target: root, types: ['pointerdown', 'mousedown', 'click'] }),
  },
];

function viewOf(doc) {
  try { return (doc && doc.defaultView) || (typeof window !== 'undefined' ? window : null); } catch (e) { return null; }
}

// DOM 순서(앞 → 뒤)로 정렬. 같은 부모면 뒤가 «위»다.
function sortByDomOrder(list) {
  try {
    return list.slice().sort((a, b) => {
      if (a.root === b.root) return 0;
      const rel = a.root.compareDocumentPosition(b.root);
      if (rel & 4) return -1;    // b 가 a 뒤 → a 먼저
      if (rel & 2) return 1;     // b 가 a 앞 → b 먼저
      return 0;
    });
  } catch (e) { return list; }
}

// 지금 열려 있는 팝업 [{root, kind}] — DOM 순서(뒤가 위). opts.skip(root) 이 참이면 뺀다(닫는 중·포기한 루트).
export function findOpenPopups(doc, opts) {
  const skip = (opts && typeof opts.skip === 'function') ? opts.skip : null;
  const out = [];
  if (!doc || typeof doc.querySelectorAll !== 'function') return out;
  POPUP_KINDS.forEach((kind) => {
    let list = [];
    try { list = doc.querySelectorAll(kind.sel); } catch (e) { list = []; }
    Array.prototype.forEach.call(list, (el) => {
      try {
        if (!kind.open(el)) return;
        if (kind.needVisible && !isVisible(el)) return;
        if (skip && skip(el)) return;
        if (out.some((p) => p.root === el)) return;   // 두 계열에 걸리면 먼저 잡힌 계열로 본다
        out.push({ root: el, kind });
      } catch (e) { /* MM DOM 변동 — 이 요소만 건너뛴다 */ }
    });
  });
  return sortByDomOrder(out);
}

// 맨 위 팝업. 🔴 K4 를 먼저 본다 — K4 는 이미 있는 #root-portal 안에 붙고 K1·K2 포털은 body 끝에
// 새로 붙어, DOM 순서만 보면 «모달 안에서 연 프로필·이모지 팝오버»보다 아래 모달이 마지막이 된다.
export function topPopup(list) {
  if (!list || !list.length) return null;
  for (let i = list.length - 1; i >= 0; i -= 1) if (list[i].kind.topFirst) return list[i];
  return list[list.length - 1];
}

// 🔴 keyCode 27 필수(MM·부트스트랩은 legacy keyCode 로 Escape 를 본다). init 으로 keyCode 를 안 받는
//    엔진이면 이벤트에 직접 심는다.
function fireKey(doc, target, type) {
  const w = viewOf(doc);
  const Ctor = (w && w.KeyboardEvent) || (typeof KeyboardEvent !== 'undefined' ? KeyboardEvent : null);
  if (!Ctor || !target) return false;
  try {
    const ev = new Ctor(type, {
      key: 'Escape', code: 'Escape', keyCode: 27, which: 27, bubbles: true, cancelable: true,
    });
    if (ev.keyCode !== 27) {
      try {
        Object.defineProperty(ev, 'keyCode', { configurable: true, get: () => 27 });
        Object.defineProperty(ev, 'which', { configurable: true, get: () => 27 });
      } catch (e) { /* 못 심으면 key·code 만으로 보낸다 */ }
    }
    target.dispatchEvent(ev);
    return true;
  } catch (e) { return false; }
}

function firePointer(doc, target, type) {
  const w = viewOf(doc);
  if (!target) return false;
  try {
    const init = { bubbles: true, cancelable: true };
    if (w) init.view = w;
    let Ctor = null;
    if (type === 'pointerdown' && w && w.PointerEvent) Ctor = w.PointerEvent;
    if (!Ctor) Ctor = (w && w.MouseEvent) || (typeof MouseEvent !== 'undefined' ? MouseEvent : null);
    if (!Ctor) return false;
    target.dispatchEvent(new Ctor(type, init));
    return true;
  } catch (e) { return false; }
}

function sendPrimary(doc, popup) {
  const target = innerTarget(popup.root);
  const ok = fireKey(doc, target, 'keydown');
  if (popup.kind.keyup) fireKey(doc, target, 'keyup');   // RootClose 는 keyup 으로 받는다
  return ok;
}

function sendFallback(doc, popup) {
  let plan = null;
  try { plan = popup.kind.fallback(doc, popup.root); } catch (e) { plan = null; }
  if (!plan || !plan.target) return false;
  plan.types.forEach((type) => firePointer(doc, plan.target, type));
  return true;
}

// doc 당 1회 설치. 반환 = dispose()(옵저버·popstate 리스너·타이머 정리). 이미 설치돼 있으면 null.
// opts: isActive()(모바일이고 화면 분할이 아닐 때만 참) · topWin(칸을 쌓을 최상위 창) · getHref()(MM 안 주소).
export function setupMmMenuBackClose(doc, options) {
  const opts = options || {};
  if (!doc || !doc.body || doc.__gwMmMenuBack) return null;
  const topWin = opts.topWin || viewOf(doc);
  if (!topWin || !topWin.history || typeof topWin.history.pushState !== 'function') return null;
  const isActive = typeof opts.isActive === 'function' ? opts.isActive : () => true;
  const getHref = typeof opts.getHref === 'function' ? opts.getHref : () => {
    try { return (doc.defaultView && doc.defaultView.location.href) || null; } catch (e) { return null; }
  };
  doc.__gwMmMenuBack = true;

  // closing = 닫기를 보낸 루트(닫힘 애니메이션 동안 DOM 에 남아도 열림으로 세지 않는다).
  // stubborn = 1차·폴백에도 안 닫힌 루트(추적에서 뺀다 — 다음 뒤로가기는 원래대로 이전 메뉴로).
  // 🔴 순회가 필요해 WeakSet 이 아니라 Set 이다. sync() 마다 «사라졌거나 원판정이 닫힘»인 것을 지운다.
  const closing = new Set();
  const stubborn = new Set();
  const timers = new Set();
  let obsTimer = null;
  let pushed = false;
  let pushedHref = null;
  let ignorePop = 0;
  let disposed = false;

  const later = (fn, ms) => {
    if (disposed) return null;
    const id = setTimeout(() => {
      timers.delete(id);
      if (disposed) return;
      try { fn(); } catch (e) { /* */ }
    }, ms);
    timers.add(id);
    return id;
  };

  const skip = (root) => closing.has(root) || stubborn.has(root);

  // 원판정 = 표의 «선택자·클래스» 판정만(가시성 재측정이 아니다).
  const rawOpen = (root) => POPUP_KINDS.some((kind) => {
    try { return root.matches(kind.sel) && kind.open(root); } catch (e) { return false; }
  });

  // 닫힌(또는 사라진) 루트를 추적에서 뺀다 → 같은 요소가 다시 열리면 처음처럼 잡는다
  // (드롭다운처럼 닫힌 채 DOM 에 남았다가 재사용되는 계열이 있다).
  const release = () => {
    [closing, stubborn].forEach((set) => {
      Array.from(set).forEach((root) => {
        let gone = false;
        try { gone = root.isConnected === false; } catch (e) { gone = true; }
        if (gone || !rawOpen(root)) set.delete(root);
      });
    });
  };

  // 닫기 절차에서 쓰는 «지금도 열려 있나»(추적 집합과 무관하게 표 그대로 본다).
  const stillOpen = (popup) => {
    const root = popup.root;
    try {
      if (root.isConnected === false) return false;
      if (!root.matches(popup.kind.sel) || !popup.kind.open(root)) return false;
      if (popup.kind.needVisible && !isVisible(root)) return false;
      return true;
    } catch (e) { return false; }
  };

  const sync = () => {
    if (disposed) return;
    release();
    if (!isActive()) return;
    let open = [];
    try { open = findOpenPopups(doc, { skip }); } catch (e) { open = []; }
    if (open.length && !pushed) {
      pushed = true;
      pushedHref = getHref();
      try {
        const cur = (topWin.history && topWin.history.state) || {};
        // 🔴 기존 state 를 펼친다(react-router 6 의 idx·key·usr 보존 → 빠져나올 때 위치 변화 0).
        // 🔴 URL 인자는 주지 않는다(인자 2개) — 주소가 그대로여야 iframe 이 다시 뜨지 않는다.
        topWin.history.pushState({ ...cur, [STATE_KEY]: true }, '');
      } catch (e) { pushed = false; }
      return;
    }
    if (!open.length && pushed) {
      // 사용자가 항목·배경으로 닫았다 → 우리가 쌓은 칸을 되돌린다.
      pushed = false;
      let st = null;
      try { st = topWin.history.state; } catch (e) { st = null; }
      if (st && st[STATE_KEY] && getHref() === pushedHref) {
        ignorePop = Date.now() + IGNORE_POP_MS;
        try { topWin.history.back(); } catch (e) { ignorePop = 0; }
      }
      // 둘 중 하나라도 어긋나면(다른 데서 칸이 바뀌었거나 MM 안에서 이동했다) 칸을 남긴다 —
      // 남의 칸을 빼앗는 것보다 빈 뒤로가기 1회가 낫다.
    }
  };

  const closeTop = () => {
    let open = [];
    try { open = findOpenPopups(doc, { skip }); } catch (e) { open = []; }
    const popup = topPopup(open);
    if (!popup) return;
    closing.add(popup.root);
    sendPrimary(doc, popup);
    later(() => {
      if (!stillOpen(popup)) return;                     // 1차로 닫혔다
      if (!sendFallback(doc, popup)) { stubborn.add(popup.root); return; }
      later(() => { if (stillOpen(popup)) stubborn.add(popup.root); }, CLOSE_WAIT_MS);
    }, CLOSE_WAIT_MS);
  };

  const onPop = () => {
    if (disposed) return;
    if (ignorePop) {
      const echo = Date.now() < ignorePop;
      ignorePop = 0;                                     // 한 번만 무시한다
      if (echo) return;
    }
    if (!pushed) return;
    let st = null;
    try { st = topWin.history.state; } catch (e) { st = null; }
    if (st && st[STATE_KEY]) return;                     // 아직 우리 칸 위다
    pushed = false;
    if (isActive()) { try { closeTop(); } catch (e) { /* */ } }
    // 닫힘 애니메이션 뒤 재점검 — 겹친 팝업이 남았으면 다시 한 칸 쌓는다.
    // (pushState 는 popstate 를 일으키지 않으므로 루프가 생기지 않는다.)
    later(sync, RESYNC_MS);
  };

  const kick = () => {
    if (disposed || obsTimer) return;                    // 🔴 쓰로틀 — 진행 중 타이머가 있으면 새로 걸지 않는다
    obsTimer = setTimeout(() => {
      obsTimer = null;
      if (disposed) return;
      try { sync(); } catch (e) { /* */ }
    }, THROTTLE_MS);
  };

  let obs = null;
  try {
    const MO = (doc.defaultView && doc.defaultView.MutationObserver)
      || (typeof window !== 'undefined' ? window.MutationObserver : null);
    if (MO) {
      obs = new MO(kick);
      // 팝업은 붙었다 떨어지기도(childList) 하고 클래스·aria-hidden 만 바뀌기도 한다.
      obs.observe(doc.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'aria-hidden'] });
    }
  } catch (e) { obs = null; }
  try { topWin.addEventListener('popstate', onPop); } catch (e) { /* */ }
  try { sync(); } catch (e) { /* */ }                    // 설치 시점에 이미 열려 있던 팝업도 잡는다

  return function dispose() {
    if (disposed) return;
    disposed = true;
    try { if (obs) obs.disconnect(); } catch (e) { /* */ }
    try { topWin.removeEventListener('popstate', onPop); } catch (e) { /* */ }
    if (obsTimer) { clearTimeout(obsTimer); obsTimer = null; }
    timers.forEach((id) => { try { clearTimeout(id); } catch (e) { /* */ } });
    timers.clear();
    closing.clear();
    stubborn.clear();
    // 🔴 history 칸·back 은 건드리지 않는다(우리가 쌓은 칸은 사용자의 뒤로가기가 쓴다).
    try { delete doc.__gwMmMenuBack; } catch (e) { doc.__gwMmMenuBack = false; }
  };
}
